import axios from 'axios';
import { getCurrentSession } from './auth';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

api.interceptors.request.use(async (config) => {
  const session = await getCurrentSession();
  if (session) {
    config.headers.Authorization = `Bearer ${session.token}`;
  }
  return config;
});

export interface Patient {
  internal_id: number;
  hm_id: string;
  created_at: string | null;
  start_date: string | null;
  status: string | null;
  clinic_id: number | null;
  doctor_id: number | null;
  case_manager_id: number | null;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  age: string | null;
  dob: string | null;
  gender: string | null;
  city: string | null;
  area: string | null;
  patient_type: string | null;
  sub_clinic: string | null;
  intake_done: boolean | null;
  active: boolean | null;
  source: string | null;
  reason_for_referral: string | null;
  red_flag: boolean | null;
  red_flag_reason: string | null;
  theraphy_finish: string | null;
  clinic_name: string | null;
  clinic_group: string | null;
  next_meeting: number | null;
  next_meeting_type_id: number | null;
  next_meeting_type: string | null;
  next_meeting_start: string | null;
  last_interaction: string | null;
  finish_invitations: boolean | null;
  invitation_status: string | null;
  last_meeting: string | null;
  renew_date: string | null;
  cm_name: string | null;
  psy_name: string | null;
  status_group: string | null;
  approved: boolean | null;
  next_meeting_scheduled: boolean | null;
  finish_reason: string | null;
  waiting_time: number;
  done_meetings: number | null;
  active_meetings: number | null;
  noshow_meetings: number | null;
  canceled_meetings: number | null;
}

export interface PatientListResponse {
  total: number;
  patients: Patient[];
}

export interface PatientExtraDetails {
  health_fund: string | null;
  last_meeting_summary: string | null;
  invite_link: string | null;
  future_meeting_type: string | null;
  future_meeting_date: string | null;
  future_yoman_link: string | null;
  meeting1_type: string | null;
  meeting1_date: string | null;
  meeting2_type: string | null;
  meeting2_date: string | null;
  meeting3_type: string | null;
  meeting3_date: string | null;
  meeting1_case_manager_name: string | null;
  meeting1_doctor_name: string | null;
  meeting1_diagnosis: string | null;
  meeting1_prescription: string | null;
  meeting1_team_url: string | null;
  meeting_summaries_history: { name: string | null; summary: string | null }[];
}

export const patientsApi = {
  list: (limit = 200) =>
    api.get<PatientListResponse>('/api/patients', { params: { limit } }),
  getExtraDetails: (internalId: number) =>
    api.get<PatientExtraDetails>(`/api/patients/${internalId}/details`),
};

export interface BotReply {
  reply: string;
  intent: string;
  command: string;
  priority: string;
  task_type: string | null;
}

export interface WatiEvent {
  received_at: string;
  payload: Record<string, unknown>;
  bot_reply: BotReply | null;
}

export interface WatiEventListResponse {
  events: WatiEvent[];
}

export const watiApi = {
  list: () => api.get<WatiEventListResponse>('/api/webhooks/wati'),
};

export interface TaliazBotV1Reply {
  reply: string;
  source: string;
  confidence: number;
  intent: string | null;
  hm_id: string | null;
  facts_found: boolean;
}

export interface TaliazBotV1Event {
  received_at: string;
  payload: Record<string, unknown>;
  bot_reply: TaliazBotV1Reply | null;
}

export interface TaliazBotV1EventListResponse {
  events: TaliazBotV1Event[];
}

export const taliazBotV1Api = {
  list: () => api.get<TaliazBotV1EventListResponse>('/api/webhooks/taliaz-botv1'),
};

export interface WhatsappAiBotEntry {
  logged_at: string | null;
  patient_id: number | null;
  hm_id: string | null;
  timestamp: string | null;
  message: string | null;
  suggested_intent: string | null;
  your_correction: string | null;
  predicted: string | null;
  final_source: string | null;
  final_reply: string | null;
  all_judge_calls_correct: boolean | null;
  judge_reasoning: string | null;
  final_answer_correct: boolean | null;
  final_answer_reasoning: string | null;
  patient_data: string | null;
}

export interface WhatsappAiBotListResponse {
  total: number;
  entries: WhatsappAiBotEntry[];
}

export const whatsappAiBotApi = {
  list: (limit = 100) =>
    api.get<WhatsappAiBotListResponse>('/api/whatsapp-ai-bot', { params: { limit } }),
  thread: (hmId: string) =>
    api.get<WhatsappAiBotListResponse>(`/api/whatsapp-ai-bot/thread/${encodeURIComponent(hmId)}`),
};

export type UserRole = 'admin' | 'viewer';

export interface AgentUser {
  username: string;
  email: string;
  role: UserRole;
  status: string;
  enabled: boolean;
}

export interface UserListResponse {
  users: AgentUser[];
}

export const usersApi = {
  list: () => api.get<UserListResponse>('/api/users'),
  invite: (email: string, role: UserRole = 'viewer') =>
    api.post<AgentUser>('/api/users', { email, role }),
  remove: (username: string) => api.delete(`/api/users/${encodeURIComponent(username)}`),
  setRole: (username: string, role: UserRole) =>
    api.patch(`/api/users/${encodeURIComponent(username)}/role`, { role }),
};

export default api;
